import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col, Dropdown } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, error, isLoading, clearError } = useAuth();
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'en', name: 'English', flag: 'EN' },
    { code: 'nl', name: 'Nederlands', flag: 'NL' }
  ];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!email || !password) {
      return;
    }

    const success = await login(email, password);
    if (success && onLoginSuccess) {
      onLoginSuccess();
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={4}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">{t('auth.loginTitle')}</h4>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm" className="border-0">
                    {languages.find(lang => lang.code === i18n.language)?.flag || 'EN'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {languages.map((lang) => (
                      <Dropdown.Item 
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        active={i18n.language === lang.code}
                      >
                        {lang.flag} {lang.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={clearError}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleLogin}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.emailLabel')}</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.passwordLabel')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.passwordPlaceholder')}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading}
                  >
                    {isLoading ? t('auth.loading') : t('auth.loginButton')}
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-3">
                <Button
                  variant="link"
                  onClick={() => window.location.href = '/wachtwoord-vergeten'}
                  disabled={isLoading}
                  className="d-block"
                >
                  {t('auth.forgotPassword')}
                </Button>
                <Button
                  variant="link"
                  onClick={() => window.location.href = '/account-aanvragen'}
                  disabled={isLoading}
                  className="d-block"
                >
                  {t('auth.requestAccount')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;